/**
 * WebAuthn PRF ceremony for offline-unlock key derivation.
 *
 * PRF (pseudo-random function) extension returns a stable per-credential secret
 * given a fixed input salt. We feed that secret to deriveKeyFromPRF to get a
 * non-extractable AES-256-GCM key. Same credential + same salt → same key, so
 * it round-trips across unlocks. The secret never leaves the device.
 *
 * Not all platforms support PRF (older iOS Safari) — callers fall back to PIN.
 */
import { config } from '../config';

// Fixed PRF eval input — domain-separates our key from any other PRF use of the
// same credential. Stable forever; changing it would orphan enrolled vaults.
const PRF_SALT = new TextEncoder().encode('checksandticks-offline-unlock-v1');

function bufToB64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
function b64UrlToBuf(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

export function isWebAuthnSupported(): boolean {
  return !!(window.PublicKeyCredential && typeof window.PublicKeyCredential === 'function');
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Enroll: create a platform credential that supports PRF, then evaluate PRF to
 * obtain the secret. Persists the credential id for later get(). Returns the PRF
 * output bytes for key derivation. Throws if PRF is unavailable or the user cancels.
 */
export async function enrollPrf(userId: string, userName: string): Promise<ArrayBuffer> {
  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { id: window.location.hostname, name: 'ChecksAndTicks' },
      user: { id: randomBytes(32), name: userName, displayName: userName },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },
        { alg: -257, type: 'public-key' },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'required', // discoverable — needed for get() without server allowList
      },
      timeout: 60000,
      attestation: 'none',
      extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('Credential creation returned null');

  const ext = credential.getClientExtensionResults() as { prf?: { enabled?: boolean } };
  if (!ext.prf?.enabled) {
    throw new Error('PRF not supported by this authenticator');
  }

  const credId = bufToB64Url(credential.rawId);
  localStorage.setItem(config.auth.vault.prfCredentialKey, credId);

  // Many authenticators do not return PRF results during create(); evaluate via get().
  return await evaluatePrf(credId);
}

/**
 * Unlock: evaluate PRF for the enrolled credential. Returns the same secret bytes
 * enroll produced. Throws if no enrolled credential or the user cancels.
 */
export async function unlockPrf(): Promise<ArrayBuffer> {
  const credId = localStorage.getItem(config.auth.vault.prfCredentialKey);
  if (!credId) throw new Error('No PRF credential enrolled');
  return await evaluatePrf(credId);
}

async function evaluatePrf(credId: string): Promise<ArrayBuffer> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [
        { id: b64UrlToBuf(credId), type: 'public-key', transports: ['internal'] },
      ],
      userVerification: 'required',
      timeout: 60000,
      extensions: {
        prf: { eval: { first: PRF_SALT } },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new Error('Assertion returned null');
  const results = (assertion.getClientExtensionResults() as {
    prf?: { results?: { first?: ArrayBuffer } };
  }).prf?.results?.first;
  if (!results) throw new Error('PRF evaluation produced no output');
  return results;
}

export function clearPrfCredential(): void {
  localStorage.removeItem(config.auth.vault.prfCredentialKey);
}
